import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Send, Users, MessageCircle, User, Image, Paperclip, X, ArrowLeft, Download, Bell, BellOff } from 'lucide-react';

const API = `${BACKEND_URL}/api`;

// Sonido de notificación estilo Apple (servido desde el backend)
const NOTIFICATION_SOUND_URL = `${BACKEND_URL}/api/sounds/notification.mp3`;

const TeamChat = ({ isOpen, onClose, currentUser }) => {
  const [activeTab, setActiveTab] = useState('group');
  const [groupMessages, setGroupMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState({ group: 0, direct: 0, total: 0 });
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const lastMessageCountRef = useRef({ group: 0, direct: 0 });

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.4;
    
    // Preload the audio
    audioRef.current.load();
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Play notification sound and show browser notification
  const playNotification = useCallback((message) => {
    if (!notificationsEnabled) return;
    
    // Play sound
    if (audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
    
    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`💬 ${message.sender_name}`, {
        body: message.content || '📎 Archivo adjunto',
        icon: '/favicon.ico',
        tag: 'team-chat'
      });
    }
  }, [notificationsEnabled]);

  // Fetch group messages
  const fetchGroupMessages = useCallback(async (checkNew = false) => {
    try {
      const response = await axios.get(`${API}/chat/group`, { withCredentials: true });
      const messages = response.data || [];
      
      // Check for new messages
      if (checkNew && messages.length > lastMessageCountRef.current.group) {
        const newMsgs = messages.slice(lastMessageCountRef.current.group);
        newMsgs.forEach(msg => {
          if (msg.sender_id !== currentUser?.id) {
            playNotification(msg);
          }
        });
      }
      lastMessageCountRef.current.group = messages.length;
      
      setGroupMessages(messages);
    } catch (error) {
      console.error('Error fetching group messages:', error);
    }
  }, [currentUser?.id, playNotification]);

  // Fetch conversations list
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, []);

  // Fetch available users
  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/users`, { withCredentials: true });
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Fetch direct messages
  const fetchDirectMessages = useCallback(async (userId, checkNew = false) => {
    try {
      const response = await axios.get(`${API}/chat/direct/${userId}`, { withCredentials: true });
      const messages = response.data.messages || [];
      
      // Check for new messages
      if (checkNew && messages.length > lastMessageCountRef.current.direct) {
        const newMsgs = messages.slice(lastMessageCountRef.current.direct);
        newMsgs.forEach(msg => {
          if (msg.sender_id !== currentUser?.id) {
            playNotification(msg);
          }
        });
      }
      lastMessageCountRef.current.direct = messages.length;
      
      setDirectMessages(messages);
      setSelectedUser({
        id: userId,
        name: response.data.other_user_name
      });
    } catch (error) {
      console.error('Error fetching direct messages:', error);
    }
  }, [currentUser?.id, playNotification]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
      setUnreadCount(response.data);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchGroupMessages();
      fetchConversations();
      fetchAvailableUsers();
      fetchUnreadCount();
      lastMessageCountRef.current = { group: 0, direct: 0 };
    }
  }, [isOpen, fetchGroupMessages, fetchConversations, fetchAvailableUsers, fetchUnreadCount]);

  // Auto-scroll
  useEffect(() => {
    scrollToBottom();
  }, [groupMessages, directMessages]);

  // Polling for new messages
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      if (activeTab === 'group') {
        fetchGroupMessages(true);
      } else if (activeTab === 'conversation' && selectedUser) {
        fetchDirectMessages(selectedUser.id, true);
      }
      fetchUnreadCount();
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, activeTab, selectedUser, fetchGroupMessages, fetchDirectMessages, fetchUnreadCount]);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewFile({
          file,
          preview: e.target?.result,
          type: 'image',
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewFile({
        file,
        preview: null,
        type: 'file',
        name: file.name
      });
    }
  };

  // Upload file
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/chat/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al subir archivo');
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !previewFile) return;
    setLoading(true);

    try {
      let attachmentData = {};
      
      // Upload file if exists
      if (previewFile) {
        setUploading(true);
        const uploadResult = await uploadFile(previewFile.file);
        attachmentData = {
          attachment_url: uploadResult.url,
          attachment_type: uploadResult.type,
          attachment_name: uploadResult.name
        };
        setUploading(false);
      }

      const messagePayload = {
        content: newMessage,
        ...attachmentData
      };

      if (activeTab === 'group') {
        await axios.post(`${API}/chat/group`, messagePayload, { withCredentials: true });
        fetchGroupMessages();
      } else if (activeTab === 'conversation' && selectedUser) {
        await axios.post(`${API}/chat/direct/${selectedUser.id}`, messagePayload, { withCredentials: true });
        fetchDirectMessages(selectedUser.id);
      }
      
      setNewMessage('');
      setPreviewFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.message || 'Error al enviar mensaje');
      setUploading(false);
    } finally {
      setLoading(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`, { withCredentials: true });
      if (activeTab === 'group') {
        fetchGroupMessages();
      } else {
        fetchDirectMessages(selectedUser.id);
      }
      toast.success('Mensaje eliminado');
    } catch (error) {
      toast.error('Error al eliminar mensaje');
    }
  };

  // Start conversation
  const startConversation = (user) => {
    setSelectedUser(user);
    lastMessageCountRef.current.direct = 0;
    fetchDirectMessages(user.id);
    setActiveTab('conversation');
  };

  // Go back
  const goBackToList = () => {
    setActiveTab('direct');
    setSelectedUser(null);
    setDirectMessages([]);
    fetchConversations();
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    }
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  };

  // Render messages
  const renderMessages = (messages) => {
    let lastDate = '';
    
    return messages.map((msg, index) => {
      const msgDate = formatDate(msg.created_at);
      const showDate = msgDate !== lastDate;
      lastDate = msgDate;
      const isOwn = msg.sender_id === currentUser?.id;

      return (
        <React.Fragment key={msg.id}>
          {showDate && (
            <div className="text-center my-3">
              <span className="text-xs bg-zinc-700 text-gray-400 px-3 py-1 rounded-full">
                {msgDate}
              </span>
            </div>
          )}
          <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}>
            <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
              {!isOwn && (
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs font-medium text-gray-400">{msg.sender_name}</span>
                  <span className={`text-xs px-1 py-0.5 rounded ${
                    msg.sender_role === 'administrativo' ? 'bg-purple-500/20 text-purple-400' :
                    msg.sender_role === 'chatter' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {msg.sender_role === 'administrativo' ? 'CEO' : msg.sender_role}
                  </span>
                </div>
              )}
              <div className={`relative px-3 py-2 rounded-2xl ${
                isOwn 
                  ? 'bg-blue-600 text-white rounded-br-md' 
                  : 'bg-zinc-700 text-white rounded-bl-md'
              }`}>
                {/* Attachment */}
                {msg.attachment_url && (
                  <div className="mb-2">
                    {msg.attachment_type === 'image' ? (
                      <img 
                        src={`${BACKEND_URL}${msg.attachment_url}`} 
                        alt={msg.attachment_name}
                        className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
                        onClick={() => window.open(`${BACKEND_URL}${msg.attachment_url}`, '_blank')}
                      />
                    ) : (
                      <a 
                        href={`${BACKEND_URL}${msg.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-black/20 rounded-lg p-2 hover:bg-black/30"
                      >
                        <Paperclip className="w-4 h-4" />
                        <span className="text-sm truncate">{msg.attachment_name}</span>
                        <Download className="w-4 h-4 ml-auto" />
                      </a>
                    )}
                  </div>
                )}
                
                {/* Content */}
                {msg.content && (
                  <p className="text-sm break-words">{msg.content}</p>
                )}
                
                <span className={`text-xs ${isOwn ? 'text-blue-200' : 'text-gray-400'} mt-1 block`}>
                  {formatTime(msg.created_at)}
                </span>
              </div>
              {isOwn && (
                <button
                  onClick={() => handleDeleteMessage(msg.id)}
                  className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-red-400 mt-1 transition-opacity"
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl h-[80vh] flex flex-col p-0">
        {/* Header */}
        <div className="p-4 border-b border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                Chat del Equipo
                {unreadCount.total > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount.total}
                  </span>
                )}
              </div>
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={`p-2 rounded-lg transition-colors ${
                  notificationsEnabled ? 'text-green-400 hover:bg-green-500/20' : 'text-gray-500 hover:bg-gray-500/20'
                }`}
                title={notificationsEnabled ? 'Desactivar notificaciones' : 'Activar notificaciones'}
              >
                {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </button>
            </DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          {activeTab !== 'conversation' && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant={activeTab === 'group' ? 'default' : 'outline'}
                onClick={() => setActiveTab('group')}
                className={activeTab === 'group' ? 'bg-blue-600' : 'border-zinc-600'}
              >
                <Users className="w-4 h-4 mr-1" />
                Grupo
                {unreadCount.group > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">
                    {unreadCount.group}
                  </span>
                )}
              </Button>
              <Button
                size="sm"
                variant={activeTab === 'direct' ? 'default' : 'outline'}
                onClick={() => setActiveTab('direct')}
                className={activeTab === 'direct' ? 'bg-blue-600' : 'border-zinc-600'}
              >
                <User className="w-4 h-4 mr-1" />
                Directos
                {unreadCount.direct > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">
                    {unreadCount.direct}
                  </span>
                )}
              </Button>
            </div>
          )}
          
          {/* Back button */}
          {activeTab === 'conversation' && (
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" variant="ghost" onClick={goBackToList} className="text-gray-400">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Volver
              </Button>
              <span className="text-white font-medium">{selectedUser?.name}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Group Chat */}
          {activeTab === 'group' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-1">
                {groupMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay mensajes aún</p>
                    <p className="text-sm">¡Sé el primero en escribir!</p>
                  </div>
                ) : (
                  renderMessages(groupMessages)
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* File Preview */}
              {previewFile && (
                <div className="px-4 py-2 border-t border-zinc-700 bg-zinc-800">
                  <div className="flex items-center gap-2">
                    {previewFile.type === 'image' ? (
                      <img src={previewFile.preview} alt="" className="w-16 h-16 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-16 bg-zinc-700 rounded flex items-center justify-center">
                        <Paperclip className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-white truncate">{previewFile.name}</p>
                      <p className="text-xs text-gray-400">{previewFile.type === 'image' ? 'Imagen' : 'Archivo'}</p>
                    </div>
                    <button 
                      onClick={() => {
                        setPreviewFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Input */}
              <div className="p-4 border-t border-zinc-700">
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-zinc-600 hover:bg-zinc-700"
                    disabled={uploading}
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 bg-zinc-800 border-zinc-600 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    disabled={uploading}
                  />
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={loading || uploading || (!newMessage.trim() && !previewFile)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {uploading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Direct Messages List */}
          {activeTab === 'direct' && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 border-b border-zinc-700">
                <p className="text-xs text-gray-400 mb-2">Iniciar conversación con:</p>
                <div className="flex flex-wrap gap-2">
                  {availableUsers.map((user) => (
                    <Button
                      key={user.id}
                      size="sm"
                      variant="outline"
                      onClick={() => startConversation(user)}
                      className="border-zinc-600 hover:bg-zinc-700"
                    >
                      <User className="w-3 h-3 mr-1" />
                      {user.name || user.username}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-2">Conversaciones recientes:</p>
                {conversations.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No hay conversaciones</p>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => startConversation({ id: conv.other_user_id, name: conv.other_user_name })}
                        className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-zinc-600 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{conv.other_user_name}</p>
                            {conv.last_message && (
                              <p className="text-xs text-gray-400 truncate max-w-[200px]">{conv.last_message}</p>
                            )}
                          </div>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">{conv.unread_count}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Direct Conversation */}
          {activeTab === 'conversation' && selectedUser && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-1">
                {directMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Inicia la conversación</p>
                  </div>
                ) : (
                  renderMessages(directMessages)
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* File Preview */}
              {previewFile && (
                <div className="px-4 py-2 border-t border-zinc-700 bg-zinc-800">
                  <div className="flex items-center gap-2">
                    {previewFile.type === 'image' ? (
                      <img src={previewFile.preview} alt="" className="w-16 h-16 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-16 bg-zinc-700 rounded flex items-center justify-center">
                        <Paperclip className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-white truncate">{previewFile.name}</p>
                    </div>
                    <button onClick={() => setPreviewFile(null)} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Input */}
              <div className="p-4 border-t border-zinc-700">
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-zinc-600 hover:bg-zinc-700"
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Mensaje para ${selectedUser.name}...`}
                    className="flex-1 bg-zinc-800 border-zinc-600 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  />
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={loading || (!newMessage.trim() && !previewFile)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeamChat;
